/**
 * src/modules/_sugestie-logika.ts — czysta logika dymka „Sugerowane odpowiedzi” (27.10.2026).
 *
 * Bez DOM i bez sieci: status na żywo (serwer + najnowsza wiadomość z otwartego czatu), stan dymka, format daty
 * „dd.mm, gg:mm” (Europe/Warsaw), HTML dymka (escape + akapity przez tekstWiadomosciHtml z PR #560), % edycji
 * przed wysyłką. Testy: tests/frontend/sugestie-dymek.test.mjs. DOM i zapytania: src/modules/sugestie-dymek.ts.
 */
import { tekstWiadomosciHtml } from './_tekst-wiadomosci.ts';

export type Wariant = { tytul: string; tresc: string; smart_niedozwolony?: boolean; naruszenia?: string[]; naruszenia_blokujace?: boolean };
export type Fakt = { klucz: string; notatka?: string };
export type Uzycie = { wariant: number; kto?: string | null; uzyto_at?: string | null; wyslano_at?: string | null; edycja_proc?: number | null; mimo_nieaktualnej?: boolean };
export type Sugestia = {
  id: number; warianty: Wariant[]; uzasadnienie: string; sprawdzone_fakty: Fakt[];
  ostatnia_wiadomosc_kupujacego_id?: string | null; ostatnia_wiadomosc_at?: string | null; utworzono_at?: string | null;
  status: 'AKTUALNA' | 'NIEAKTUALNA' | 'ZASTAPIONA'; powody: string[]; uzycia?: Uzycie[];
};
export type WiadomoscCzatu = { id?: string | null; at?: string | null; rola?: string | null; auto?: boolean };
export type DaneDymka = {
  wlaczone: boolean; biezaca: Sugestia | null; historia: Sugestia[]; liczba_historii: number;
  najnowsza_wiadomosc?: WiadomoscCzatu | null; oczekuje_kupujacy?: boolean | null;
  /** Scalenie 27.10: dymek „Asystent” aktywny dla firmy → treść pokazuje dymek, karta w czacie tylko pasek / ukryta. */
  panel_asystenta?: boolean; karta_w_czacie?: 'pasek' | 'ukryta' | 'pelna';
};
export type StanDymka = 'ukryty' | 'aktualna' | 'nieaktualna' | 'czeka';
export type Ui = { rozwiniety: boolean; historia: boolean; uzyto: { wariant: number; tytul: string } | null };

const FAKTY: Record<string, string> = {
  smart: 'Smart', przesylki: 'Przesyłka', ponowne_wysylki: 'Ponowne wysyłki', doreczenie: 'Doręczenie',
  terminy: 'Termin', stan_magazynu: 'Stan magazynu', notatki: 'Notatki',
};
const TOLERANCJA_MS = 1000;

function esc(s: any): string {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c: string) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

function czas(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(String(iso));
  return Number.isFinite(t) ? t : null;
}

/** „27.10, 06:12” w strefie Europe/Warsaw (niezależnie od strefy przeglądarki). */
export function dataGodzina(iso?: string | null): string {
  const t = czas(iso);
  if (t == null) return '—';
  const cz = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
    .formatToParts(new Date(t));
  const p = (typ: string) => (cz.find((x) => x.type === typ) || { value: '' }).value;
  return p('day') + '.' + p('month') + ', ' + p('hour') + ':' + p('minute');
}

/** Rola autora: czat dyskusji (author.role) albo Centrum Wiadomości (author.isInterlocutor). */
export function rolaAutora(m: any): string {
  const a = (m && m.author) || {};
  if (a.isInterlocutor === true) return 'BUYER';
  if (a.isInterlocutor === false) return 'SELLER';
  return String(a.role || a.type || '').toUpperCase();
}

/** Najnowsza wiadomość z czatu (Allegro: dyskusja/reklamacja albo wątek Centrum Wiadomości). */
export function ostatniaZCzatu(wiadomosci: any[]): WiadomoscCzatu | null {
  const lista = (Array.isArray(wiadomosci) ? wiadomosci : []).filter((m: any) => {
    if (!m || m._pending || m._failed || String(m.id || '').startsWith('_tmp_')) return false;
    const rola = rolaAutora(m);
    return rola === 'BUYER' || rola === 'SELLER' || String(m.text || '').trim() !== '';
  });
  if (!lista.length) return null;
  const m = lista.slice().sort((a: any, b: any) => String(a.createdAt || '').localeCompare(String(b.createdAt || ''))).pop();
  return { id: m.id ? String(m.id) : null, at: m.createdAt || null, rola: rolaAutora(m), auto: !!(m._is_auto_reply || m.auto_reply) };
}

function powodWiadomosci(rola?: string | null): string {
  const r = String(rola || '').toUpperCase();
  if (r === 'BUYER' || r === 'INTERLOCUTOR') return 'nowa_wiadomosc_kupujacego';
  if (r === 'SELLER' || r === 'SELLER_AUTO') return 'nowa_nasza_wiadomosc';
  return 'nowe_zdarzenie_platformy';
}

/** Status sugestii: serwer (wiadomości w bazie, status, przesyłki) + najnowsza wiadomość widoczna w czacie na żywo. */
export function statusNaZywo(s: Sugestia, ost: WiadomoscCzatu | null): { status: 'AKTUALNA' | 'NIEAKTUALNA'; powody: string[] } {
  const powody = (s.powody || []).slice();
  if (ost && (ost.id || ost.at)) {
    const znane = ost.id && s.ostatnia_wiadomosc_kupujacego_id && String(ost.id) === String(s.ostatnia_wiadomosc_kupujacego_id);
    const tOst = czas(ost.at);
    const tSug = czas(s.ostatnia_wiadomosc_at);
    if (!znane && tOst != null && tSug != null && tOst > tSug + TOLERANCJA_MS) {
      const p = powodWiadomosci(ost.rola);
      if (!powody.includes(p)) powody.push(p);
    }
  }
  return { status: s.status === 'AKTUALNA' && !powody.length ? 'AKTUALNA' : 'NIEAKTUALNA', powody };
}

/** Czy kupujący czeka na odpowiedź: czat na żywo ma pierwszeństwo przed bazą. */
export function oczekujeKupujacy(d: DaneDymka | null, ost: WiadomoscCzatu | null): boolean | null {
  if (ost && ost.rola) {
    const r = String(ost.rola).toUpperCase();
    if (r === 'BUYER' || r === 'INTERLOCUTOR') return true;
    if (r === 'SELLER' || r === 'SELLER_AUTO') return !!ost.auto || r === 'SELLER_AUTO';
  }
  return d ? (d.oczekuje_kupujacy == null ? null : !!d.oczekuje_kupujacy) : null;
}

export function stanDymka(d: DaneDymka | null, ost: WiadomoscCzatu | null): StanDymka {
  if (!d || !d.wlaczone) return 'ukryty';
  const b = d.biezaca;
  if (b && statusNaZywo(b, ost).status === 'AKTUALNA') return 'aktualna';
  if (oczekujeKupujacy(d, ost) === false) return 'czeka';
  return b ? 'nieaktualna' : 'ukryty';
}

export function tekstPlakietki(status: string, powody: string[]): string {
  if (status === 'AKTUALNA') return 'AKTUALNA';
  if (powody.some((p) => p.startsWith('nowa_') || p === 'nowe_zdarzenie_platformy')) return 'NIEAKTUALNA — jest nowsza wiadomość';
  if (powody.includes('zmiana_statusu')) return 'NIEAKTUALNA — zmienił się status sprawy';
  if (powody.includes('zmiana_przesylki')) return 'NIEAKTUALNA — zmieniła się przesyłka';
  if (powody.includes('zmiana_sprawy_na_platformie')) return 'NIEAKTUALNA — sprawa zmieniła się na platformie';
  return 'NIEAKTUALNA';
}

/** Treść wariantu → akapity <p> (pusta linia), pojedyncze \n → <br>; escape w tekstWiadomosciHtml. */
export function akapityHtml(tresc: string): string {
  return String(tresc || '').replace(/\r\n?/g, '\n').split(/\n{2,}/).map((a) => a.trim()).filter(Boolean)
    .map((a) => '<p>' + tekstWiadomosciHtml(a) + '</p>').join('');
}

/** Tekst do pola odpowiedzi: zwykły tekst, akapity i nowe linie zachowane (bez HTML). */
export function tekstDoPola(tresc: string): string {
  return String(tresc || '').replace(/\r\n?/g, '\n').trim();
}

/** Procent zmian między wklejonym a wysłanym tekstem (0 = bez zmian, 100 = wszystko inne). */
export function procentEdycji(wklejony: string, wyslany: string): number {
  const a = tekstDoPola(wklejony);
  const b = tekstDoPola(wyslany);
  if (a === b) return 0;
  const dl = Math.max(a.length, b.length);
  if (!dl) return 0;
  if (a.length > 3000 || b.length > 3000) {
    // Długie teksty: przybliżenie — wspólny początek i koniec, reszta liczona jako zmiana.
    let p = 0;
    while (p < a.length && p < b.length && a[p] === b[p]) p++;
    let k = 0;
    while (k < a.length - p && k < b.length - p && a[a.length - 1 - k] === b[b.length - 1 - k]) k++;
    return Math.min(100, Math.round(((dl - p - k) / dl) * 100));
  }
  let poprz = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) poprz[j] = j;
  for (let i = 1; i <= a.length; i++) {
    const biez = new Array(b.length + 1);
    biez[0] = i;
    for (let j = 1; j <= b.length; j++) {
      biez[j] = Math.min(poprz[j] + 1, biez[j - 1] + 1, poprz[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    poprz = biez;
  }
  return Math.min(100, Math.round((poprz[b.length] / dl) * 100));
}

function plakietka(status: string, powody: string[]): string {
  const ok = status === 'AKTUALNA';
  return '<span class="sb-sug-plakietka sb-sug-plakietka--' + (ok ? 'zielona' : 'czerwona') + '" role="status" data-sug="plakietka">'
    + '<i data-lucide="' + (ok ? 'check' : 'alert-circle') + '" aria-hidden="true"></i>' + esc(tekstPlakietki(status, powody)) + '</span>';
}

function wynikUzycia(u?: Uzycie[]): string {
  const uz = (u || []).filter(Boolean);
  if (!uz.length) return '<span class="sb-sug-wynik sb-sug-wynik--nie"><i data-lucide="circle-dashed" aria-hidden="true"></i>nie użyto</span>';
  const o = uz[uz.length - 1];
  return '<span class="sb-sug-wynik sb-sug-wynik--uzyto"><i data-lucide="check" aria-hidden="true"></i>użyto wariantu ' + o.wariant
    + (o.edycja_proc != null ? ', edycja ' + o.edycja_proc + '%' : '') + (o.kto ? ' · ' + esc(o.kto) : '') + '</span>';
}

function historiaHtml(d: DaneDymka): string {
  const h = d.historia || [];
  if (!h.length) return '';
  return '<div class="sb-sug-historia" data-sug="historia"><div class="sb-sug-historia-tytul"><i data-lucide="history" aria-hidden="true"></i>Historia sugestii ('
    + d.liczba_historii + ')<span>starsze, zastąpione</span></div><ul>'
    + h.map((s) => '<li class="sb-sug-hist-poz"><div class="sb-sug-hist-gora">' + wynikUzycia(s.uzycia)
      + '<span class="sb-sug-hist-czas">' + esc(dataGodzina(s.utworzono_at)) + ' · do wiadomości z ' + esc(dataGodzina(s.ostatnia_wiadomosc_at)) + '</span></div>'
      + '<div class="sb-sug-hist-tytuly">' + s.warianty.length + ' ' + (s.warianty.length === 1 ? 'wariant' : 'warianty') + ': '
      + s.warianty.map((w) => '„' + esc(w.tytul) + '”').join(', ') + '</div>'
      + '<details class="sb-sug-hist-szczegoly"><summary>Pokaż warianty</summary>'
      + s.warianty.map((w, i) => '<div class="sb-sug-hist-wariant"><strong>' + (i + 1) + '. ' + esc(w.tytul) + '</strong>' + akapityHtml(w.tresc) + '</div>').join('')
      + '</details></li>').join('')
    + '</ul></div>';
}

function stopka(d: DaneDymka, ui: Ui, s?: Sugestia | null): string {
  const hist = d.liczba_historii > 0
    ? '<button type="button" class="sb-sug-historia-btn" data-sug-akcja="historia" aria-expanded="' + (ui.historia ? 'true' : 'false') + '">'
      + '<i data-lucide="history" aria-hidden="true"></i>Historia sugestii (' + d.liczba_historii + ')</button>'
    : '';
  return '<div class="sb-sug-stopka"><span class="sb-sug-ai">AI</span><span>Asystent AI (Grok Bot)'
    + (s && s.utworzono_at ? ' · ' + esc(dataGodzina(s.utworzono_at)) : '') + '</span>' + hist + '</div>';
}

/** Cały dymek jako HTML (czysta funkcja). Pusty string = dymek ukryty. */
/** Pasek w czacie, gdy treść sugestii pokazuje dymek „Asystent” (jedna rama UI, bez dublowania treści). */
export function htmlPaska(d: DaneDymka, ost: WiadomoscCzatu | null): string {
  const s = d.biezaca as Sugestia;
  const st = statusNaZywo(s, ost);
  return '<section class="sb-sug sb-sug--pasek" data-stan="pasek" aria-label="Sugerowane odpowiedzi"><div class="sb-sug-pasek">'
    + '<i data-lucide="sparkles" aria-hidden="true"></i><strong>Sugestia gotowa</strong>' + plakietka(st.status, st.powody)
    + '<button type="button" class="sb-sug-link" data-sug-akcja="asystent">Otwórz w panelu Asystent</button></div></section>';
}

export function htmlDymka(d: DaneDymka | null, ost: WiadomoscCzatu | null, ui: Ui): string {
  const stan = stanDymka(d, ost);
  if (stan === 'ukryty' || !d) return '';
  if (d.panel_asystenta && d.karta_w_czacie && d.karta_w_czacie !== 'pelna') {
    // jedna rama UI: warianty, „Użyj”, historia i „Czeka na klienta” pokazuje dymek „Asystent”
    if (d.karta_w_czacie === 'ukryta' || stan === 'czeka' || !d.biezaca) return '';
    return htmlPaska(d, ost);
  }
  if (stan === 'czeka') {
    return '<section class="sb-sug" data-stan="czeka" aria-label="Sugerowane odpowiedzi">'
      + '<div class="sb-sug-pasek"><i data-lucide="clock" aria-hidden="true"></i><strong>Czeka na klienta</strong>'
      + '<span class="sb-sug-pasek-opis">Ostatnia wiadomość jest od nas — nowa sugestia pojawi się po odpowiedzi kupującego.</span></div>'
      + (d.liczba_historii > 0 || d.biezaca ? stopka({ ...d, liczba_historii: d.liczba_historii + (d.biezaca ? 1 : 0) }, ui, null) : '')
      + (ui.historia ? historiaHtml({ ...d, historia: (d.biezaca ? [d.biezaca] : []).concat(d.historia || []), liczba_historii: d.liczba_historii + (d.biezaca ? 1 : 0) }) : '')
      + '</section>';
  }
  const s = d.biezaca as Sugestia;
  const st = statusNaZywo(s, ost);
  const ok = st.status === 'AKTUALNA';
  if (ui.uzyto && ok) {
    return '<section class="sb-sug" data-stan="uzyto" aria-label="Sugerowane odpowiedzi"><div class="sb-sug-pasek">'
      + '<i data-lucide="check-circle-2" aria-hidden="true"></i><span>Wklejono wariant ' + ui.uzyto.wariant + ' „' + esc(ui.uzyto.tytul)
      + '” do pola odpowiedzi — sprawdź i wyślij sam.</span>'
      + '<button type="button" class="sb-sug-link" data-sug-akcja="pokaz">Pokaż sugestie</button></div></section>';
  }
  const nowsza = !ok && ost && ost.at && st.powody.some((p) => p.startsWith('nowa_')) ? ost.at
    : (!ok && d.najnowsza_wiadomosc && d.najnowsza_wiadomosc.at ? d.najnowsza_wiadomosc.at : null);
  let html = '<section class="sb-sug" data-stan="' + (ok ? 'aktualna' : 'nieaktualna') + '" aria-label="Sugerowane odpowiedzi" data-sug-id="' + s.id + '">'
    + '<div class="sb-sug-naglowek"><i data-lucide="sparkles" class="sb-sug-ikona" aria-hidden="true"></i><strong class="sb-sug-tytul-karty">Sugerowane odpowiedzi</strong>'
    + plakietka(st.status, st.powody)
    + '<button type="button" class="sb-sug-zwin" data-sug-akcja="zwin" aria-expanded="' + (ui.rozwiniety ? 'true' : 'false') + '" title="' + (ui.rozwiniety ? 'Zwiń' : 'Rozwiń') + '">'
    + '<i data-lucide="' + (ui.rozwiniety ? 'chevron-down' : 'chevron-right') + '" aria-hidden="true"></i></button></div>'
    + '<div class="sb-sug-zakres" data-sug="zakres"><i data-lucide="clock" aria-hidden="true"></i>Uwzględnia całą rozmowę do wiadomości z ' + esc(dataGodzina(s.ostatnia_wiadomosc_at)) + '</div>';
  if (!ok && nowsza) {
    html += '<div class="sb-sug-zakres sb-sug-zakres--blad"><i data-lucide="alert-circle" aria-hidden="true"></i>Nowsza wiadomość: <strong>' + esc(dataGodzina(nowsza)) + '</strong></div>';
  }
  if (!ui.rozwiniety) {
    return html + '<div class="sb-sug-zwiniete"><button type="button" class="sb-sug-link" data-sug-akcja="zwin" aria-expanded="false">'
      + 'Pokaż warianty (' + s.warianty.length + ')</button></div></section>';
  }
  html += '<div class="sb-sug-cialo"><ol class="sb-sug-warianty">';
  s.warianty.forEach((w, i) => {
    const nr = i + 1;
    const klasa = ok ? (i === 0 ? 'sb-sug-uzyj' : 'sb-sug-uzyj sb-sug-uzyj--drugi') : 'sb-sug-uzyj sb-sug-uzyj--szary';
    const opis = ok ? '' : ' aria-describedby="sb-sug-dop-' + s.id + '-' + nr + '"';
    html += '<li class="sb-sug-wariant"><div class="sb-sug-wariant-gora"><span class="sb-sug-nr">' + nr + '</span><span class="sb-sug-wariant-tytul">' + esc(w.tytul) + '</span>'
      + '<span class="sb-sug-uzyj-wrap"><button type="button" class="' + klasa + '" data-sug-uzyj="' + nr + '"' + opis + '>Użyj</button>'
      + (ok ? '' : '<span class="sb-sug-uzyj-dopisek" id="sb-sug-dop-' + s.id + '-' + nr + '">wymaga potwierdzenia</span>') + '</span></div>'
      + '<div class="sb-sug-tresc">' + akapityHtml(w.tresc) + '</div>'
      + ((w.naruszenia || []).length ? '<div class="sb-sug-zakres sb-sug-zakres--blad" data-sug="naruszenia"><i data-lucide="alert-circle" aria-hidden="true"></i>'
        + esc((w.naruszenia || []).join(' ')) + '</div>' : '')
      + (w.smart_niedozwolony ? '<div class="sb-sug-zakres sb-sug-zakres--blad" data-sug="smart-niedozwolony"><i data-lucide="alert-circle" aria-hidden="true"></i>'
        + 'Wspomina Allegro Smart, a dla tej sprawy Smart jest niedozwolony — usuń wzmiankę, podaj adres do odesłania.</div>' : '')
      + '</li>';
  });
  html += '</ol>';
  if (s.uzasadnienie) {
    html += '<details class="sb-sug-szczegoly"><summary>Uzasadnienie</summary><p class="sb-sug-uzasadnienie">' + tekstWiadomosciHtml(s.uzasadnienie) + '</p></details>';
  }
  const fakty = s.sprawdzone_fakty || [];
  if (fakty.length) {
    html += '<details class="sb-sug-szczegoly"><summary>Sprawdzone fakty<span class="sb-sug-liczba">' + fakty.length + '</span></summary><div class="sb-sug-fakty">'
      + fakty.map((f) => '<span class="sb-sug-fakt"><i data-lucide="check" aria-hidden="true"></i>' + esc(FAKTY[f.klucz] || f.klucz)
        + (f.notatka ? '<span class="sb-sug-fakt-wartosc"> · ' + esc(f.notatka) + '</span>' : '') + '</span>').join('') + '</div></details>';
  }
  if (ui.historia) html += historiaHtml(d);
  html += '</div>' + stopka(d, ui, s) + '<p class="sb-sug-uwaga">Asystent nigdy nie wysyła sam — „Użyj” wkleja tekst do pola, wysyłasz Ty.</p></section>';
  return html;
}
